import type { LSFontFamily } from "@packages/sdk";

import { colors, constantSemanticColors, shades, tints } from "./colors";
import {
  dropShadow,
  fontFamilies,
  fontSizes,
  fontWeights,
  lineHeights,
  numericMilliseconds,
  numericPercentages,
  numericPixels,
  numericValues,
  numericViewportHeight,
  numericViewportWidth,
  radius,
  spacing,
  spacingMasks,
  stroke,
  zIndices,
} from "./tokens";

// Define the semantic colors for light and dark themes
const lightSemanticColors = {
  background: colors.white,
  primary: colors.black,
  secondary: colors.white,
  neutralsHighest: colors.grey10,
  neutralsVeryHigh: colors.grey20,
  neutralsHigher: colors.grey30,
  neutralsHigh: colors.grey40,
  neutralsMedHigh: colors.grey50,
  neutralsMedium: colors.grey60,
  neutralsMedLow: colors.grey70,
  neutralsLow: colors.grey80,
  neutralsLower: colors.grey90,
  neutralsVeryLow: colors.grey95,
  neutralsLowest: colors.grey99,
  onOverlayBackground: colors.white,
  onOverlayHighest: colors.black,
  onOverlayMedHigh: colors.grey30,
  onOverlayMedium: colors.grey60,
  onOverlayMedLow: colors.grey90,
  onOverlayLow: colors.grey99,
  onOverlayLowest: colors.white,
  accentHighest: colors.purple10,
  accentVeryHigh: colors.purple20,
  accentHigher: colors.purple30,
  accentHigh: colors.purple40,
  accentMedHigh: colors.purple50,
  accentMedium: colors.purple60,
  accentMedLow: colors.purple70,
  accentLow: colors.purple80,
  accentLower: colors.purple90,
  accentVeryLow: colors.purple95,
  accentLowest: colors.purple99,
  successSuccess: colors.green60,
  successOnSuccess: colors.white,
  successSuccessContainer: colors.green95,
  successOnSuccessContainer: colors.green40,
  lowerContrastHighest: tints.tint90,
  lowerContrastVeryHigh: tints.tint80,
  lowerContrastHigher: tints.tint70,
  lowerContrastHigh: tints.tint60,
  lowerContrastMedHigh: tints.tint50,
  lowerContrastMed: tints.tint40,
  lowerContrastMedLow: tints.tint30,
  lowerContrastLow: tints.tint20,
  lowerContrastLower: tints.tint15,
  lowerContrastVeryLow: tints.tint10,
  lowerContrastLowest: tints.tint5,
  errorHighest: colors.red10,
  errorVeryHigh: colors.red20,
  errorHigher: colors.red30,
  errorHigh: colors.red40,
  errorMedHigh: colors.red50,
  errorMedium: colors.red60,
  errorMedLow: colors.red70,
  errorLow: colors.red80,
  errorLower: colors.red90,
  errorVeryLow: colors.red95,
  errorLowest: colors.red99,
  errorError: colors.red60,
  errorOnError: colors.white,
  errorErrorContainer: colors.red95,
  errorOnErrorContainer: colors.red40,
  higherContrastHighest: shades.shade90,
  higherContrastVeryHigh: shades.shade80,
  higherContrastHigher: shades.shade70,
  higherContrastHigh: shades.shade60,
  higherContrastMedHigh: shades.shade50,
  higherContrastMed: shades.shade40,
  higherContrastMedLow: shades.shade30,
  higherContrastLow: shades.shade20,
  higherContrastLower: shades.shade15,
  higherContrastVeryLow: shades.shade10,
  higherContrastLowest: shades.shade5,
  blackWhiteOpacity50: "#808080",
  wordBackground: colors.lightBlue,
  wordBackgroundHC: colors.deepBlue,
  wordCardText: "inherit",
  wordCardTextHC: colors.black,
  wordSuccess: "#14B977",
  wordSuccessHC: "#2DE199",
  wordPartial: colors.yellow,
  wordPartialHC: "#FFCE2D",
  wordNever: constantSemanticColors.fixedLightenLower,
};

const darkSemanticColors = {
  background: colors.blackOLED,
  primary: colors.white,
  secondary: colors.black,
  neutralsHighest: colors.grey99,
  neutralsVeryHigh: colors.grey95,
  neutralsHigher: colors.grey90,
  neutralsHigh: colors.grey80,
  neutralsMedHigh: colors.grey70,
  neutralsMedium: colors.grey60,
  neutralsMedLow: colors.grey50,
  neutralsLow: colors.grey40,
  neutralsLower: colors.grey30,
  neutralsVeryLow: colors.grey20,
  neutralsLowest: colors.grey10,
  onOverlayBackground: colors.grey20,
  onOverlayHighest: colors.white,
  onOverlayMedHigh: colors.grey99,
  onOverlayMedium: colors.grey80,
  onOverlayMedLow: colors.grey50,
  onOverlayLow: colors.grey30,
  onOverlayLowest: colors.grey20,
  accentHighest: colors.purple99,
  accentVeryHigh: colors.purple95,
  accentHigher: colors.purple90,
  accentHigh: colors.purple80,
  accentMedHigh: colors.purple70,
  accentMedium: colors.purple60,
  accentMedLow: colors.purple50,
  accentLow: colors.purple40,
  accentLower: colors.purple30,
  accentVeryLow: colors.purple20,
  accentLowest: colors.purple10,
  successSuccess: colors.green60,
  successOnSuccess: colors.black,
  successSuccessContainer: colors.green20,
  successOnSuccessContainer: colors.green80,
  lowerContrastHighest: shades.shade90,
  lowerContrastVeryHigh: shades.shade80,
  lowerContrastHigher: shades.shade70,
  lowerContrastHigh: shades.shade60,
  lowerContrastMedHigh: shades.shade50,
  lowerContrastMed: shades.shade40,
  lowerContrastMedLow: shades.shade30,
  lowerContrastLow: shades.shade20,
  lowerContrastLower: shades.shade15,
  lowerContrastVeryLow: shades.shade10,
  lowerContrastLowest: shades.shade5,
  errorHighest: colors.red99,
  errorVeryHigh: colors.red95,
  errorHigher: colors.red90,
  errorHigh: colors.red80,
  errorMedHigh: colors.red70,
  errorMedium: colors.red60,
  errorMedLow: colors.red50,
  errorLow: colors.red40,
  errorLower: colors.red30,
  errorVeryLow: colors.red20,
  errorLowest: colors.red10,
  errorError: colors.red60,
  errorOnError: colors.black,
  errorErrorContainer: colors.red20,
  errorOnErrorContainer: colors.red80,
  higherContrastHighest: tints.tint90,
  higherContrastVeryHigh: tints.tint80,
  higherContrastHigher: tints.tint70,
  higherContrastHigh: tints.tint60,
  higherContrastMedHigh: tints.tint50,
  higherContrastMed: tints.tint40,
  higherContrastMedLow: tints.tint30,
  higherContrastLow: tints.tint20,
  higherContrastLower: tints.tint15,
  higherContrastVeryLow: tints.tint10,
  higherContrastLowest: tints.tint5,
  blackWhiteOpacity50: colors.grey80,
  wordBackground: colors.lightBlue,
  wordBackgroundHC: colors.deepBlue,
  wordCardText: "inherit",
  wordCardTextHC: colors.black,
  wordSuccess: "#14B977",
  wordSuccessHC: "#2DE199",
  wordPartial: colors.yellow,
  wordPartialHC: "#FFCE2D",
  wordNever: constantSemanticColors.fixedLightenLower,
};

// Define the base theme structure
export const baseTheme = {
  colors,
  shades,
  tints,
  constantSemanticColors,
  numericPercentages,
  numericMilliseconds,
  numericPixels,
  numericValues,
  numericViewportWidth,
  numericViewportHeight,
  dropShadow,
  spacingMasks,
  radius,
  spacing,
  stroke,
  fontSizes,
  fontWeights,
  lineHeights,
  zIndices,
};

// Create the light theme
const lightTheme = {
  ...baseTheme,
  semanticColors: lightSemanticColors,
  mode: "light",
};

const serifFonts = { sansSerif: "serif", serif: "serif" };
const sansSerifFonts = { sansSerif: "sans-serif", serif: "sans-serif" };

export const lightNormalTheme = {
  ...lightTheme,
  fontFamilies,
};

export const lightSerifTheme = {
  ...lightTheme,
  fontFamilies: serifFonts,
};

export const lightSansTheme = {
  ...lightTheme,
  fontFamilies: sansSerifFonts,
};

// Create the dark theme
const darkTheme = {
  ...baseTheme,
  semanticColors: darkSemanticColors,
  mode: "dark",
};

export const darkNormalTheme = {
  ...darkTheme,
  fontFamilies,
};

export const darkSerifTheme = {
  ...darkTheme,
  fontFamilies: serifFonts,
};

export const darkSansTheme = {
  ...darkTheme,
  fontFamilies: sansSerifFonts,
};
// Export the themes
export const themes: Record<Theme, Record<LSFontFamily, ThemeType>> = {
  light: {
    normal: lightNormalTheme,
    serif: lightSerifTheme,
    sans_serif: lightSansTheme,
  },
  dark: {
    normal: darkNormalTheme,
    serif: darkSerifTheme,
    sans_serif: darkSansTheme,
  },
  match: null, // This will be determined at runtime based on system preferences
};

// Type for the theme
export type Theme = "light" | "dark" | "match";

// Type for the theme object
export type ThemeType = typeof lightNormalTheme;
